import { captureEvent } from "@sentry/react";
import {
  type BudgetovacPositionFragment,
  UpdateBudgetovacPositionDocument,
  type UpdateBudgetovacPositionMutation,
  type UpdateBudgetovacPositionMutationVariables,
} from "@src/__generated__/graphql";
import { BudgetPositionFormStore } from "@src/components/modules/budgets/positions/stores/base";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable } from "mobx";

type UpdateDisclosureData = {
  position?: BudgetovacPositionFragment;
};

export class BudgetPositionUpdateStore extends BudgetPositionFormStore {
  private updateMutator = new MutationHelper<
    UpdateBudgetovacPositionMutation,
    UpdateBudgetovacPositionMutationVariables
  >(UpdateBudgetovacPositionDocument);

  disclosure: DisclosureState<UpdateDisclosureData>;

  constructor(private appStore: AppStore) {
    super();
    makeObservable(this);

    this.disclosure = new DisclosureState<UpdateDisclosureData>({
      onOpen: (additionalData) => {
        this.form.$.name.onChange(additionalData?.position?.name ?? "");
        this.form.$.department_id.onChange(
          additionalData?.position?.department_id ?? undefined,
        );
      },
      onClose: () => {
        this.form.reset();
      },
    });
  }

  @computed
  get isSubmitting(): boolean {
    return this.updateMutator.isLoading.value;
  }

  @action.bound
  async updatePosition(): Promise<boolean> {
    const positionId = this.disclosure.additionalData?.position?.id;

    if (!positionId) {
      captureEvent({
        message: "FE: Missing position ID while trying to update position.",
      });
      this.appStore.UIStore.toast({ status: "error" });
      return false;
    }

    const res = await this.form.validate();
    if (res.hasError) return false;

    const [, error] = await this.updateMutator.mutate({
      input: {
        id: positionId,
        name: res.value.name.$,
        department_id: res.value.department_id.$ ?? null,
      },
    });

    if (error) return false;

    this.disclosure.onClose();
    return true;
  }
}
